
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4 text-center font-sans">
          <div className="bg-white p-6 md:p-8 rounded-3xl shadow-2xl max-w-2xl w-full border border-gray-100">
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-100 mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
            </div>
            <h1 className="text-2xl font-black text-gray-900 mb-2">Oups ! Une erreur est survenue.</h1>
            <p className="text-gray-500 mb-6 font-medium">Nous sommes désolés, l'application a rencontré un problème inattendu dans cette section.</p>
            
            <div className="bg-gray-900 rounded-2xl p-5 text-left mb-6 border border-gray-800 shadow-inner overflow-hidden">
                <p className="text-red-400 font-bold mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    Détails techniques :
                </p>
                <div className="overflow-auto max-h-60 custom-scrollbar">
                    <code className="text-xs text-gray-300 font-mono break-words block">
                        {this.state.error?.toString()}
                    </code>
                    {this.state.errorInfo && (
                        <pre className="text-[10px] text-gray-500 font-mono mt-4 leading-relaxed whitespace-pre-wrap opacity-80">
                            {this.state.errorInfo.componentStack}
                        </pre>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-3">
                <button
                onClick={() => window.location.reload()}
                className="w-full py-4 bg-red-600 text-white font-black rounded-2xl hover:bg-red-700 transition-all shadow-lg shadow-red-200 active:scale-[0.98] flex items-center justify-center gap-2"
                >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Recharger l'application
                </button>
                
                <button 
                  onClick={() => window.history.back()}
                  className="w-full py-3 bg-gray-100 text-gray-600 font-bold rounded-2xl hover:bg-gray-200 transition-all active:scale-[0.98]"
                >
                    Retourner en arrière
                </button>
            </div>

            <p className="mt-6 text-[10px] text-gray-400 uppercase tracking-widest font-bold">Yumii v1.5.6 - Diagnostic Mode</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
